import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Plus, Trash2, Clock, Video, Crown, Eye, Heart } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RitualsStrip } from "@/components/Rituals";

const EMPTY = { title: "", theme_tag: "", date: "", time: "", description: "", image: "", location: "", session_type: "group" };

const SESSION_TYPES = {
  group: { label: "Group Lounge", hint: "Everyone together on camera" },
  speed_date: { label: "Speed-Date 1-on-1", hint: "Members get paired into private 1-on-1 video, rotating" },
  request: { label: "1-on-1 by Request", hint: "Members request a private 1-on-1 slot" },
  booking: { label: "Book a 1-on-1", hint: "Book a private session with a creator (free during beta)" },
};

const LOUNGES = [
  { type: "watch", title: "Watch Lounge", sub: "Pick a performer to watch. Go on camera anytime.", icon: Eye },
  { type: "group", title: "Group Lounge", sub: "Everyone on camera together — mingle & flirt.", icon: Users },
  { type: "one_on_one", title: "1-on-1 Lounge", sub: "Choose someone for a private session.", icon: Heart },
];

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const rsvp = async (id) => {
    const { data } = await api.post(`/events/${id}/rsvp`);
    toast.success(data.is_rsvped ? "You're on the list 🔥" : "RSVP removed");
    load();
  };

  const requestSession = async (ev) => {
    try {
      const { data } = await api.post(`/events/${ev.id}/request-session`);
      toast.success(data.already ? "You've already requested this 💌" : "Request sent — we'll pair you up 💌");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send request"); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", form);
      toast.success("Event added to the calendar");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/events/${id}`);
    toast.success("Event removed");
    load();
  };

  return (
    <div className="p-6 md:p-12 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-1">The Empire Calendar</div>
          <h2 className="font-heading text-3xl md:text-4xl">Themed Nights</h2>
        </div>
        {isAdmin && (
          <button data-testid="new-event-btn" onClick={() => setShowForm((s) => !s)}
            className="btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2">
            <Plus size={18} /> New Event
          </button>
        )}
      </div>

      {/* Weekly recurring rituals */}
      <RitualsStrip />

      {/* VIP Live Lounges */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-1">
          <Crown size={16} className="text-[#D4AF37]" />
          <span className="overline">VIP Live Lounges · open now</span>
        </div>
        <p className="text-sm text-[#A0A0A0] mb-4">Jump into a live video lounge anytime — no schedule needed.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {LOUNGES.map((l, i) => (
            <motion.button key={l.type} data-testid={`lounge-card-${l.type}`}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => navigate(`/lounge/${l.type}`)}
              className="pe-card rounded-2xl p-5 text-left flex flex-col gap-2 hover:border-[#D4AF37]">
              <l.icon className="text-[#D4AF37]" size={26} />
              <div className="font-heading text-2xl">{l.title}</div>
              <div className="text-sm text-[#A0A0A0]">{l.sub}</div>
              <span className="mt-2 text-xs gold-text font-semibold">Enter lounge →</span>
            </motion.button>
          ))}
        </div>
      </div>

      {isAdmin && showForm && (
        <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          onSubmit={createEvent} className="pe-card rounded-2xl p-6 mb-8 space-y-3">
          <input data-testid="event-title" required placeholder="Title (e.g. Kinky Mondays)" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} className="pe-input w-full rounded-lg px-4 py-2" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="event-tag" required placeholder="Theme tag (BDSM, Dating…)" value={form.theme_tag}
              onChange={(e) => setForm({ ...form, theme_tag: e.target.value })} className="pe-input rounded-lg px-4 py-2" />
            <input data-testid="event-location" placeholder="Location" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} className="pe-input rounded-lg px-4 py-2" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="event-date" required type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} className="pe-input rounded-lg px-4 py-2" />
            <input data-testid="event-time" type="time" value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })} className="pe-input rounded-lg px-4 py-2" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] mb-1 block">Night format</label>
            <select data-testid="event-session-type" value={form.session_type}
              onChange={(e) => setForm({ ...form, session_type: e.target.value })}
              className="pe-input w-full rounded-lg px-4 py-2 bg-[#141414]">
              {Object.entries(SESSION_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label} — {v.hint}</option>
              ))}
            </select>
          </div>
          <input data-testid="event-image" placeholder="Image URL (optional)" value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })} className="pe-input w-full rounded-lg px-4 py-2" />
          <textarea data-testid="event-desc" required placeholder="Description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} className="pe-input w-full rounded-lg px-4 py-2 h-24" />
          <button data-testid="save-event" type="submit" className="btn-gold px-6 py-2 rounded-full text-sm">Publish event</button>
        </motion.form>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {events.map((ev, i) => (
          <motion.div key={ev.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`event-${ev.id}`} className="pe-card rounded-2xl overflow-hidden">
            {ev.image && <div className="h-40 overflow-hidden"><img src={ev.image} alt={ev.title} className="w-full h-full object-cover" /></div>}
            <div className="p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs px-3 py-1 rounded-full gold-gradient text-black font-medium">{ev.theme_tag}</span>
                {ev.session_type && ev.session_type !== "group" && (
                  <span data-testid={`session-badge-${ev.id}`} className="text-xs px-3 py-1 rounded-full border border-[#E63946]/50 text-[#E63946] flex items-center gap-1">
                    <Heart size={12} /> {SESSION_TYPES[ev.session_type]?.label || "1-on-1"}
                  </span>
                )}
              </div>
              <h3 className="font-heading text-2xl mt-3">{ev.title}</h3>
              <p className="text-sm text-[#A0A0A0] mt-2 leading-relaxed">{ev.description}</p>
              <div className="flex flex-wrap gap-3 text-xs text-[#A0A0A0] mt-4">
                <span className="flex items-center gap-1"><CalendarDays size={14} className="text-[#D4AF37]" /> {ev.date}</span>
                {ev.time && <span className="flex items-center gap-1"><Clock size={14} className="text-[#D4AF37]" /> {ev.time}</span>}
                {ev.location && <span className="flex items-center gap-1"><MapPin size={14} className="text-[#D4AF37]" /> {ev.location}</span>}
                <span className="flex items-center gap-1"><Users size={14} className="text-[#D4AF37]" /> {ev.rsvp_count} going</span>
              </div>
              <div className="flex items-center gap-2 mt-5">
                {(ev.session_type === "request" || ev.session_type === "booking") ? (
                  <button data-testid={`request-session-${ev.id}`} onClick={() => requestSession(ev)}
                    className="flex-1 rounded-full py-2 text-sm font-medium btn-gold flex items-center justify-center gap-1">
                    <Heart size={15} /> {ev.session_type === "booking" ? "Book a 1-on-1" : "Request a 1-on-1"}
                  </button>
                ) : (
                  <>
                    <button data-testid={`rsvp-${ev.id}`} onClick={() => rsvp(ev.id)}
                      className={`flex-1 rounded-full py-2 text-sm font-medium transition-all ${ev.is_rsvped ? "btn-outline-gold" : "btn-gold"}`}>
                      {ev.is_rsvped ? "You're going ✓" : "RSVP"}
                    </button>
                    {ev.is_rsvped && (
                      <button data-testid={`join-live-${ev.id}`}
                        onClick={() => ev.session_type === "speed_date" ? navigate("/lounge/one_on_one") : navigate(`/event/${ev.id}/live`, { state: { title: ev.title } })}
                        className="flex-1 rounded-full py-2 text-sm font-medium btn-gold flex items-center justify-center gap-1">
                        <Video size={15} /> {ev.session_type === "speed_date" ? "Start pairing" : "Join Live"}
                      </button>
                    )}
                  </>
                )}
                {isAdmin && (
                  <button data-testid={`delete-${ev.id}`} onClick={() => remove(ev.id)}
                    className="w-10 h-10 rounded-full border border-[#E63946] text-[#E63946] flex items-center justify-center hover:bg-[#E63946]/10">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
